import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { QrCode, Globe, MapPin, TrendUp, Sparkle } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/DashboardLayout";
import { PageHeader, Spinner } from "../components/ui-bits";
import { api, getMerchantId, formatDate } from "../lib/api";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-navy/10 shadow-luxe p-6">
    <div className="h-10 w-10 rounded-xl bg-navy/5 flex items-center justify-center"><Icon size={20} weight="light" className="text-navy" /></div>
    <p className="mt-4 font-playfair text-3xl text-navy">{value}</p>
    <p className="text-[0.68rem] uppercase tracking-[0.18em] text-navy/45 mt-1">{label}</p>
  </div>
);

export default function Analytics() {
  const merchantId = getMerchantId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/analytics/scans?merchant_id=${merchantId}`);
      setData(data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seed = async () => {
    setSeeding(true);
    try {
      await api.post(`/demo/seed-scans?merchant_id=${merchantId}`);
      toast.success("Sample scan activity added");
      load();
    } catch {
      toast.error("Could not add sample data");
    } finally {
      setSeeding(false);
    }
  };

  const maxCountry = data?.countries?.[0]?.count || 1;

  return (
    <DashboardLayout>
      <PageHeader
        overline="Engagement"
        title="Passport Analytics"
        subtitle="Where and how often your Digital Product Passports are being scanned worldwide."
        action={
          <button data-testid="seed-scans-button" onClick={seed} disabled={seeding} className="flex items-center gap-2 bg-white border border-navy/15 text-navy rounded-full px-5 py-3 text-sm hover:border-gold hover:text-gold transition-colors disabled:opacity-60 self-start">
            {seeding ? <Spinner className="h-4 w-4" /> : <Sparkle size={16} weight="light" />} Add Sample Scans
          </button>
        }
      />

      {loading ? (
        <div className="h-64 rounded-2xl bg-white/60 border border-navy/5 animate-pulse" />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="analytics-stats">
            <Stat icon={QrCode} label="Total Scans" value={data.totalScans} />
            <Stat icon={Globe} label="Countries Reached" value={data.uniqueCountries} />
            <Stat icon={TrendUp} label="Top Passport Scans" value={data.topPassports[0]?.scans || 0} />
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mt-6">
            {/* trend */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-navy/10 shadow-luxe p-7">
              <h2 className="text-lg text-navy mb-5">Scans · Last 14 Days</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.series} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C9A84C" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#C9A84C" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} tick={{ fontSize: 11, fill: "#1B2B5E88" }} axisLine={false} tickLine={false} minTickGap={24} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: "1px solid #1B2B5E22", fontSize: 12 }}
                      labelFormatter={(d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}
                    />
                    <Area type="monotone" dataKey="scans" stroke="#C9A84C" strokeWidth={2.5} fill="url(#gold)" isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* countries */}
            <div className="bg-white rounded-2xl border border-navy/10 shadow-luxe p-7">
              <h2 className="text-lg text-navy mb-5">By Country</h2>
              {data.countries.length === 0 ? (
                <p className="text-sm text-navy/40">No scans yet.</p>
              ) : (
                <div className="space-y-4" data-testid="country-breakdown">
                  {data.countries.slice(0, 6).map((c) => (
                    <div key={c.country}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-navy/70 flex items-center gap-1.5"><MapPin size={13} weight="light" className="text-gold" /> {c.country}</span>
                        <span className="text-navy/50">{c.count}</span>
                      </div>
                      <div className="h-1.5 bg-navy/5 rounded-full overflow-hidden">
                        <div className="h-full bg-gold rounded-full transition-all duration-700" style={{ width: `${(c.count / maxCountry) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            {/* top passports */}
            <div className="bg-white rounded-2xl border border-navy/10 shadow-luxe p-7">
              <h2 className="text-lg text-navy mb-5">Most Scanned Passports</h2>
              {data.topPassports.length === 0 ? (
                <p className="text-sm text-navy/40">No scans yet.</p>
              ) : (
                <div className="space-y-3">
                  {data.topPassports.map((p, i) => (
                    <div key={p.certificateId} className="flex items-center gap-3">
                      <span className="font-playfair text-lg text-gold w-5">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-navy truncate">{p.productName}</p>
                        <p className="text-[0.68rem] text-navy/40 font-mono truncate">{p.certificateId}</p>
                      </div>
                      <span className="text-sm text-navy/60">{p.scans}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* recent scans */}
            <div className="bg-white rounded-2xl border border-navy/10 shadow-luxe p-7">
              <h2 className="text-lg text-navy mb-5">Recent Scans</h2>
              {data.recentScans.length === 0 ? (
                <p className="text-sm text-navy/40">No scans yet. Add sample data to preview this.</p>
              ) : (
                <div className="space-y-3">
                  {data.recentScans.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 text-sm">
                      <MapPin size={15} weight="light" className="text-gold shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-navy truncate">{s.productName}</p>
                        <p className="text-[0.68rem] text-navy/40">{s.city ? `${s.city}, ` : ""}{s.country}</p>
                      </div>
                      <span className="text-[0.68rem] text-navy/40 shrink-0">{formatDate(s.date)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
